<template>
<div :class="compact ? 'sla-inline' : 'card p-1 sla-card'">
    <div class="d-flex align-items-center justify-content-between" :class="compact ? 'mb-50' : 'mb-1'">
    <strong v-if="!compact">Tiempo SLA</strong>
    <b-badge pill class="sla-prio-badge" :class="[priorityBadgeClass, { 'ml-auto': compact }]">
        Prioridad: {{ slaPriorityLabel }}
    </b-badge>
    </div>

    <!-- Barra tipo vida -->
    <div class="sla-bar" :class="compact ? '' : 'mt-1'">
    <div
        class="sla-bar-fill"
        :class="slaLevelClass"
        :style="{ width: (slaExpired ? 100 : slaPercent) + '%' }"
    ></div>
    </div>

    <div class="d-flex justify-content-between mt-50">
    <small :class="slaExpired ? 'text-danger font-weight-bold' : 'text-muted'">
        {{ statusLabel }}
    </small>
    <small :class="slaExpired ? 'text-danger font-weight-bold' : 'text-muted'">
        {{ slaExpired ? ("+" + overtimeFormatted) : (slaPercent + "% restante") }}
    </small>
    </div>

    <div v-if="slaExpired" class="text-danger small font-weight-bold mt-50">
    ¡SLA vencido hace {{ overtimeFormatted }}!
    </div>
</div>
</template>

<script>
import { formatSlaDuration } from "@/views/helpdesk/slaFormat";
import { BBadge } from "bootstrap-vue";

export default {
name: "SlaBarCard",
components: { BBadge },
props: {
    totalSeconds: { type: Number, default: 3600 },
    remainingSeconds: { type: Number, default: 3600 },
    running: { type: Boolean, default: true },
    priority: { type: String, default: "medium" }, // "low" | "medium" | "high" | "critical" (color)
    priorityLabel: { type: String, default: null }, // nombre real de la prioridad (Bajo, Medio, Alto, Crítico)
    compact: { type: Boolean, default: false },
},
data() {
    return {
    remaining: this.remainingSeconds,
    overtime: 0, // segundos transcurridos DESPUÉS de vencer
    slaRunning: this.running,
    timer: null,
    };
},
computed: {
    slaPercent() {
    if (this.totalSeconds <= 0) return 0;
    const pct = (this.remaining / this.totalSeconds) * 100;
    return Math.max(0, Math.min(100, Math.round(pct * 100) / 100));
    },
    slaExpired() {
    return this.remaining <= 0;
    },
    slaLevelClass() {
    if (this.slaExpired) return "sla-expired";
    if (this.slaPercent > 50) return "sla-ok";
    if (this.slaPercent > 20) return "sla-warn";
    return "sla-danger";
    },
    statusLabel() {
    if (this.slaExpired) return "Vencido";
    return this.slaRunning ? "En curso" : "Detenido";
    },
    remainingFormatted() {
    return this.format(this.remaining);
    },
    overtimeFormatted() {
    return this.format(this.overtime);
    },
    totalFormatted() {
    return this.format(this.totalSeconds);
    },
    // Etiqueta que se envía al header del ticket:
    // mientras corre = tiempo restante; al vencer = tiempo total del SLA (fijo).
    headerLabel() {
    return this.slaExpired ? this.totalFormatted : this.remainingFormatted;
    },
    slaPriorityLabel() {
    if (this.priorityLabel) return this.priorityLabel;
    const map = { low: "Baja", medium: "Media", high: "Alta", critical: "Crítica" };
    return map[this.priority] || "Media";
    },
    priorityBadgeClass() {
    const map = {
        low: "prio-low",       // verde claro
        medium: "prio-medium", // amarillo
        high: "prio-high",     // naranja
        critical: "prio-critical", // rojo
    };
    return map[this.priority] || "prio-medium";
    },
},
watch: {
    running(val) {
    this.slaRunning = val;
    val ? this.startTimer() : this.stopTimer();
    },
    remainingSeconds(val) {
    this.remaining = val;
    this.overtime = 0;
    this.emitTick();
    },
},
mounted() {
    this.emitTick(); // valor inicial para el header
    if (this.slaRunning) this.startTimer();
},
beforeDestroy() {
    this.stopTimer();
},
methods: {
    format(totalSeconds) {
    return formatSlaDuration(totalSeconds);
    },
    emitTick() {
    this.$emit("tick", {
        remaining: this.remaining,
        overtime: this.overtime,
        expired: this.slaExpired,
        label: this.headerLabel,
    });
    },
    startTimer() {
    this.stopTimer();
    this.timer = setInterval(() => {
        if (this.remaining > 0) {
        this.remaining -= 1;
        if (this.remaining === 0) this.$emit("expired");
        } else {
        // ya venció: cuenta el tiempo extra hacia arriba
        this.overtime += 1;
        }
        this.emitTick();
    }, 1000);
    },
    stopTimer() {
    if (this.timer) {
        clearInterval(this.timer);
        this.timer = null;
    }
    },
},
};
</script>

<style scoped>
.sla-card {
min-width: 220px;
}
.sla-inline {
width: 100%;
}

/* Barra tipo vida */
.sla-bar {
width: 100%;
height: 10px;
border-radius: 6px;
background: #ebe9f1;
overflow: hidden;
}
.sla-inline .sla-bar {
height: 6px;
}
.sla-bar-fill {
height: 100%;
border-radius: 6px;
transition: width 0.4s ease, background-color 0.4s ease;
}

/* Niveles */
.sla-bar-fill.sla-ok {
background-color: #28c76f;
}
.sla-bar-fill.sla-warn {
background-color: #ff9f43;
}
.sla-bar-fill.sla-danger {
background-color: #ea5455;
animation: sla-pulse 1s ease-in-out infinite;
}

/* Vencido: rojo más opaco + fade suave */
.sla-bar-fill.sla-expired {
background-color: #fb9090;
animation: sla-fade 1.6s ease-in-out infinite;
}

@keyframes sla-pulse {
0%,
100% {
    opacity: 1;
}
50% {
    opacity: 0.55;
}
}

@keyframes sla-fade {
0%,
100% {
    opacity: 0.6;
}
50% {
    opacity: 0.2;
}
}
/* Colores de prioridad: ver bloque <style> global al final (necesario para
    vencer las clases .badge del tema). */
</style>
<!-- Estilos NO scoped: los colores de prioridad deben vencer a las clases
        .badge del tema (Vuexy/Bootstrap), que se inyectan globalmente. -->
<style>
.sla-prio-badge.badge {
    border: 0 !important;
}
.sla-prio-badge.badge.prio-critical {
    background-color: #ea5455 !important; /* rojo */
    color: #ffffff !important;
}
.sla-prio-badge.badge.prio-high {
    background-color: #ff9f43 !important; /* naranja */
    color: #ffffff !important;
}
.sla-prio-badge.badge.prio-medium {
    background-color: #ffc94d !important; /* amarillo */
    color: #4b4b4b !important;
}
.sla-prio-badge.badge.prio-low {
    background-color: #ededed !important; /* verde claro */
    color: #123c26 !important;
}
</style>